/**
 * Business entities — anything that the EC2 API persists in Postgres
 * (firms, team, projects, billing, tasks, expenses, organizations…).
 *
 * Split from src/shared/types.ts on 2026-04-19. types.ts re-exports from
 * here so existing `import { ... } from '../shared/types'` keeps working.
 */

// ─── Edit History (cross-cutting; lives here so both forma and spec can import it) ───

export interface EditHistoryEntry {
  userName: string;
  userId?: string;
  timestamp: string; // ISO string
  action: 'created' | 'edited' | 'published' | 'duplicated';
  /** What happened, in words, when the action alone does not say it
   *  ("Review: 3 accepted, 1 rejected from Firm Standard"). */
  note?: string;
}

// ─── Firms (tenants) ───

export interface Firm {
  id: string;
  name: string;
  slug: string;
  ownerUserId?: string;
  authMode: 'email' | 'entra';
  /** Org-chart line routing style. Persisted firm-wide so every member sees
   *  the same look. Falls back to 'smoothstep' when null. */
  orgEdgeStyle?: 'smoothstep' | 'step' | 'straight' | 'bezier';
  createdAt: string;
  updatedAt: string;
}

export interface Invitation {
  id: string;
  firmId?: string;
  email: string;
  name?: string;
  roleId?: string;
  billingRoleId?: string;
  hourlyRate?: number;
  invitedBy: string;
  status: 'pending' | 'accepted' | 'expired' | 'cancelled';
  createdAt: string;
  expiresAt?: string;
}

// ─── Privilege Roles (app access / permissions) ───

export interface Role {
  id: string;
  firmId?: string;
  name: string;
  accessLevel: 'site_admin' | 'admin' | 'manager' | 'member';
  // Granular permissions (JSONB array of permission keys)
  permissions: string[];
  // Legacy boolean columns (kept in sync for backward compat)
  canAccessAdmin: boolean;
  canAccessTeam: boolean;
  canAccessInvoices: boolean;
  canAccessAllProjects: boolean;
  canAccessBilling: boolean;
  canManageSpecs: boolean;
  canEditProjects: boolean;
  canDeleteProjects: boolean;
  canDeleteClients: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

// ─── Billing Roles (rate cards for invoicing) ───

/** Title — was "BillingRole" prior to v2.9.0 (migration 015 merged
 *  company_roles into this table).  The interface name stays as
 *  BillingRole to avoid a sweeping rename, but UI labels everywhere
 *  read "Title".  Each row optionally carries:
 *
 *    - defaultRate   the billable $/hr (gated by team_members.view_details)
 *    - displayOnly   true means "directory/HR title only — never billed"
 *                    (auto-set on rows imported from company_roles)
 *    - abbreviation  short tag for grids ("SE", "PM")
 *    - color         accent for pills and grouping
 */
export interface BillingRole {
  id: string;
  firmId?: string;
  name: string; // e.g. "Engineer I", "Senior PM", "BIM Manager"
  defaultRate?: number; // default $/hr — null for display-only titles
  /** Compact tag used in grids and badges. */
  abbreviation?: string;
  /** Hex color for the title pill / badge. */
  color?: string;
  /** True when this Title is directory-only (no billing rate). The
   *  invoice generator excludes display-only titles from rate-card
   *  dropdowns by default. */
  displayOnly?: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

// ─── Bill Rates ───

export interface BillRate {
  id: string;
  firmId?: string;
  roleId?: string;
  teamMemberId?: string;
  rate: number;
  effectiveDate: string;
  createdAt: string;
  updatedAt: string;
}

// ─── Project Types ───

export interface ProjectType {
  id: string;
  firmId?: string;
  name: string;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

// ─── Billing Types ───

export interface BillingType {
  id: string;
  firmId?: string;
  name: string;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

// ─── Activity Codes (Ajera "Description") ───

export interface ActivityCode {
  id: string;
  firmId?: string;
  name: string;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

// ─── Overhead Categories (admin-configurable) ───

export interface OverheadCategory {
  id: string;
  firmId?: string;
  name: string;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

// ─── Vacation / Time-Off ───

export interface VacationCategory {
  id: string;
  firmId?: string;
  name: string;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface FirmHoliday {
  id: string;
  firmId?: string;
  name: string;
  date: string;
  hours: number;
  createdAt: string;
  updatedAt: string;
}

export type VacationRequestStatus = 'pending' | 'approved' | 'denied';

export interface VacationRequest {
  id: string;
  firmId?: string;
  teamMemberId: string;
  vacationCategoryId: string;
  startDate: string;
  endDate: string;
  hoursPerDay: number;
  totalHours?: number;
  note?: string;
  status: VacationRequestStatus;
  approvedBy?: string;
  approvedAt?: string;
  deniedBy?: string;
  deniedAt?: string;
  denialReason?: string;
  createdAt: string;
  updatedAt: string;
}

// ─── Expense Types (admin-configurable) ───

export interface ExpenseType {
  id: string;
  firmId?: string;
  name: string;
  rate?: number; // per-unit rate (e.g. $/mile); null = user enters flat amount
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

// ─── Organizations ───

export interface OrgAddress {
  label?: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  primary?: boolean;
}

export interface OrgContact {
  name: string;
  title?: string;
  email?: string;
  phone?: string;
  primary?: boolean;
}

export interface Organization {
  id: string;
  firmId?: string;
  name: string;
  shortName?: string;
  type?: 'client' | 'owner' | 'both' | 'other';
  phone?: string;
  email?: string;
  website?: string;
  addresses?: OrgAddress[];
  contacts?: OrgContact[];
  defaultFormaId?: string;
  rwbPoc?: string;
  clientManagerId?: string;
  industry?: string; // deprecated — use industries[]
  industries?: string[]; // multiple industries per client
  fileStoragePath?: string; // path to client's file directory on shared drive
  notes?: string;
  archivedAt?: string | null;
  archivedBy?: string | null;
  createdAt: string;
  updatedAt: string;
}

// ─── Company Roles (firm-defined titles with optional badges) ───

export interface CompanyRole {
  id: string;
  firmId?: string;
  name: string;
  shortCode?: string; // badge letters (e.g., "P", "AP", "PM", "SA")
  badgeColor?: string; // hex color for badge
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

// ─── Team Members ───

/** Per-person privilege overrides — null means "use role default" */
export interface PrivilegeOverrides {
  canAccessAdmin?: boolean | null;
  canAccessTeam?: boolean | null;
  canAccessInvoices?: boolean | null;
  canAccessAllProjects?: boolean | null;
  canAccessBilling?: boolean | null;
  canManageSpecs?: boolean | null;
  canEditProjects?: boolean | null;
  canDeleteProjects?: boolean | null;
  canDeleteClients?: boolean | null;
}

export interface TeamMember {
  id: string;
  firmId?: string;
  name: string;
  /** First/middle/last — People Rebuild (migration 038, member_profiles
   *  side table; team_members is at the Postgres column cap). `name`
   *  stays the canonical display name and is rebuilt server-side from
   *  these whenever any part is present in a save payload. */
  firstName?: string;
  middleName?: string;
  lastName?: string;
  email?: string;
  /** Secondary email — People Rebuild grant #4 (member_profiles side
   *  table). HAR sync and other email matchers accept primary OR this. */
  secondaryEmail?: string;
  role?: string;
  roleId?: string; // privilege role
  billingRoleId?: string; // billing role (for rate cards)
  companyRoleId?: string; // company role / title (e.g., Principal, Sr Associate)
  /** Department (MEPT, BIM, etc) — drives forecasting grid grouping and team-page columns.
   *  Distinct from `teams` membership: a person belongs to ONE department but can be on
   *  MANY teams. */
  departmentId?: string;
  /** Drives capacity-bar coloring on forecasts and timesheets. Default 'salary'. */
  employmentType?: 'salary' | 'full_time_hourly' | 'part_time_hourly';
  /** Weekly capacity hours (default 40). Overrides per-employment-type defaults. */
  weeklyCapacityHours?: number;
  reportsTo?: string;
  hourlyRate?: number; // override rate; if unset, uses billingRole.defaultRate
  active: boolean;
  privilegeOverrides?: PrivilegeOverrides;
  orgX?: number; // freeform org chart X position
  orgY?: number; // freeform org chart Y position
  // Org-chart visual edges and per-edge customization now live in the
  // `org_chart_edges` table (migration 022). The previous `orgEdgeMeta`
  // and `additionalManagerIds` fields are gone — see [[org_chart_edge]].
  createdAt: string;
  updatedAt: string;
}

/** Department lookup — sortable list (MEPT, BIM, etc). One per team_member. */
export interface Department {
  id: string;
  firmId?: string;
  name: string;
  abbreviation?: string;
  color?: string;
  sortOrder?: number;
  createdAt?: string;
  updatedAt?: string;
}

/** Project budget row — three nesting levels in one table.
 *    NULL phase_id            → project-total budget
 *    phase_id, NULL role_id   → phase-total budget
 *    phase_id + role_id       → finest-grain budget (e.g. SD × Engineer I) */
export interface ProjectBudget {
  id: string;
  firmId?: string;
  projectId: string;
  phaseId?: string;
  billingRoleId?: string;
  budgetHours?: number;
  budgetAmount?: number;
  notes?: string;
  createdAt?: string;
  updatedAt?: string;
}

/** Per-user saved view for any list/grid surface. `config` is freeform —
 *  each surface defines its own shape (columns, filters, grouping). */
export interface DashboardView {
  id: string;
  firmId?: string;
  userId: string;
  /** Logical surface ID — 'forecasting', 'projects', 'reports.utilization', etc. */
  surface: string;
  name: string;
  config: Record<string, any>;
  isDefault?: boolean;
  shared?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface ProjectAssignment {
  id: string;
  firmId?: string;
  projectId: string;
  teamMemberId: string;
  role?: string; // 'lead' | 'member' | custom
  createdAt: string;
}

// ─── Teams (named groups of members) ───

export interface Team {
  id: string;
  firmId?: string;
  name: string;
  description?: string;
  color?: string; // hex color for team identification
  shortCode?: string; // discipline badge (e.g. "M", "E", "P", "T")
  parentTeamId?: string; // nullable — for nesting teams within teams
  createdAt: string;
  updatedAt: string;
}

export interface TeamAssignment {
  id: string;
  firmId?: string;
  teamId: string;
  teamMemberId: string;
  teamRole: 'leader' | 'member';
  createdAt: string;
}

// ─── Org chart edges ───

/**
 * One visual line on the org chart. Decoupled from `team_members.reportsTo`
 * so users can:
 *   • Draw any number of lines into a single member (multi-parent)
 *   • Pick dashed/solid/dotted per line (not derived from data shape)
 *   • Color, label, and route each line independently
 *
 * `team_members.reportsTo` still tracks "the primary manager" for the
 * rest of the system (permissions, hierarchy traversal). The client
 * keeps reportsTo in sync with the first / primary edge.
 */
export interface OrgChartEdge {
  id: string;
  firmId?: string;
  /** Which canvas this edge belongs to (migration 041, Josh grant
   *  2026-08-17: "views become separate canvases/boards"). Undefined/null
   *  = the implicit Main view (the pre-existing chart). Set = a custom
   *  view/board — the edge is drawn ONLY there, purely visual, never
   *  hierarchy (reports_to stays Main-only truth). */
  viewId?: string | null;
  /** Source / target ids. When the corresponding *Type is 'team' the
   *  id is a team's id; otherwise it's a team_member id. */
  sourceMemberId: string;
  targetMemberId: string;
  /** Discriminator: which table the id points at. Defaults to 'member'
   *  on the server so historical rows continue to behave the same. */
  sourceType?: 'member' | 'team' | 'junction';
  targetType?: 'member' | 'team' | 'junction';
  /** Line style. 'solid' is the default; 'dashed' / 'dotted' for
   *  co-management / informal-reporting visuals. Purely visual — does
   *  not change how the system treats the relationship. */
  style?: 'solid' | 'dashed' | 'dotted';
  /** Optional accent color (CSS color string). Falls back to var(--bd). */
  color?: string;
  /** Line thickness in px. Defaults to 1.5. */
  thickness?: number;
  /** Optional inline label rendered as a chip mid-edge. */
  label?: string;
  /** Side of the source node the line leaves from. Auto-picks closest
   *  face when unset. */
  sourceHandle?: 'top' | 'bottom' | 'left' | 'right';
  /** Side of the target node the line attaches to. */
  targetHandle?: 'top' | 'bottom' | 'left' | 'right';
  /** Manually-placed waypoints. Empty/undefined = auto-route. */
  waypoints?: { x: number; y: number }[];
  createdAt: string;
  updatedAt: string;
}

/**
 * A junction node on the org chart. Acts as a multi-way connection
 * point — N lines can terminate at a junction, M lines can leave
 * from it. Used to consolidate scenarios like "everyone reports to
 * the three managing principals": one line from each principal into
 * the junction, one line from the junction to each of the many
 * reports. Junctions are draggable, snappable, and have a small
 * visual footprint compared to a full person node.
 */
export interface OrgChartJunction {
  id: string;
  firmId?: string;
  /** Which canvas this junction belongs to (migration 041). Undefined/
   *  null = Main view; set = a custom view/board — see OrgChartEdge.viewId. */
  viewId?: string | null;
  x: number;
  y: number;
  label?: string;
  color?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * A free-floating text-box note on the org chart (migration 040, Josh
 * grant 2026-08-17). Decoupled from every other chart entity — not a
 * member, team, junction, or edge — just a piece of text a user drops
 * anywhere on the canvas. Same edit-session model as edges/junctions:
 * created/moved/edited/deleted client-side during a session, landed in
 * the `orgchart-commit` transaction on Save.
 */
export interface OrgChartNote {
  id: string;
  firmId?: string;
  /** Which canvas this note belongs to (migration 041). Undefined/null =
   *  Main view; set = a custom view/board — see OrgChartEdge.viewId. */
  viewId?: string | null;
  x: number;
  y: number;
  /** Explicit width in px. Undefined/null = auto-size to content. */
  width?: number;
  text: string;
  /** px. Renderer clamps 8–72; server clamps independently on write. */
  fontSize: number;
  /** CSS color string. Undefined/null = theme default (var(--tx)). */
  color?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Who can SEE a saved org-chart view in the picker. Data-driven gating
 * (migration 039) — deliberately not a permission-catalog key, to avoid
 * colliding with the parallel permissions-audit session. Enforced
 * server-side in ec2-api/server.js (filterOrgChartViews); the shapes below
 * are the full contract the server understands.
 */
export type OrgChartViewVisibility =
  | { mode: 'everyone' }
  | { mode: 'roles'; roleIds: string[] }
  | { mode: 'private' };

/**
 * A saved org-chart VIEW — as of migration 041 (Josh grant 2026-08-17,
 * "views become separate canvases/boards") a view is a full SEPARATE
 * CANVAS: it starts blank and gets its own member placements
 * (OrgChartViewPosition), lines (OrgChartEdge.viewId), junctions
 * (OrgChartJunction.viewId), and text boxes (OrgChartNote.viewId). The
 * pre-existing chart (no view row at all) is the implicit "Main" view —
 * Main's data keeps living where it always has (team_members.org_x/org_y,
 * and edges/junctions/notes rows with viewId null). reportsTo remains
 * org-wide truth driven ONLY by Main; a custom view's lines are visual
 * arrangement, never hierarchy. `config`/`visibility` (display prefs —
 * node shape/density/badges, routing style, team-box visibility, snap)
 * are unchanged by this and remain opaque to the server.
 */
export interface OrgChartView {
  id: string;
  firmId?: string;
  name: string;
  config: Record<string, unknown>;
  visibility: OrgChartViewVisibility;
  createdBy?: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * One member's placement on a custom canvas view (migration 041). Composite
 * identity is (viewId, memberId) — a member has at most one position per
 * view. Only exists for CUSTOM views; the Main view's positions still live
 * on `team_members.orgX/orgY`, never here. Write-only via `orgchart-commit`
 * (see commitOrgChart.ts) — there is no single-row save/delete path, so this
 * type has no `id` field.
 */
export interface OrgChartViewPosition {
  viewId: string;
  memberId: string;
  firmId?: string;
  x: number;
  y: number;
  createdAt: string;
  updatedAt: string;
}

// ─── Timesheets ───

export interface Timesheet {
  id: string;
  firmId?: string;
  teamMemberId: string;
  weekStart: string; // ISO date (Monday)
  status: 'draft' | 'submitted' | 'approved' | 'locked';
  submittedAt?: string;
  approvedBy?: string;
  approvedAt?: string;
  lockedAt?: string;
  lockedBy?: string;
  /** Reject sends the sheet back to 'draft' (there's no dedicated
   *  'rejected' status) — these record who/why (Tranche 0 item 2/7,
   *  server action timesheet_reject, migration 042). */
  rejectedBy?: string;
  rejectedAt?: string;
  rejectionReason?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface TimesheetAuditEntry {
  id: string;
  firmId?: string;
  timesheetId: string;
  action:
    | 'created'
    | 'submitted'
    | 'unsubmitted'
    | 'approved'
    | 'rejected'
    | 'locked'
    | 'unlocked'
    | 'edited';
  performedBy: string;
  performedByName?: string;
  details?: string;
  createdAt: string;
}

// ─── Projects ───

export interface ProjectSelections {
  [masterId: string]: {
    enabledSystems: string[];
    options: Record<string, any>;
    parameters: Record<string, any>;
  };
}

export interface Project {
  id: string;
  name: string;
  projectNumber?: string;
  /** The project's name as it appears in the specifications / on the cover &
   *  headers — often different from the firm's internal `name`. Resolves the
   *  {Project.ClientProjectName} forma token. */
  clientProjectName?: string;
  clientName?: string;
  clientOrgId?: string;
  ownerOrgId?: string;
  status?: 'active' | 'closed';
  projectTypeId?: string;
  billingTypeId?: string;
  activityCodeId?: string;
  masters?: string[];
  selections: ProjectSelections;
  contractValue?: number;
  anticipatedHours?: number;
  anticipatedExpenses?: number;
  picId?: string;
  pmId?: string;
  /** Absolute path to this project's folder on the firm's Project Drive. Populated
   * either by auto-matching the firm's project-drive root or manually per project. */
  folderPath?: string;
  /** Currently-active project phase. Set by PM / billing manager via the
   *  project editor; the server's stamp_phase_from_project trigger uses this
   *  value to populate `phase_id` on every hour_forecasts / hour_entries
   *  insert/update. NULL = no active phase = project-level allocation.
   *  Migration 017. */
  currentPhaseId?: string | null;
  archivedAt?: string | null;
  archivedBy?: string | null;
  /** Migration 053: stamped when a project's status flips to 'closed' (mirrors
   *  archivedAt/archivedBy's shape). NULL while status is 'active'. */
  closedAt?: string | null;
  closedBy?: string | null;
  createdAt: string;
  updatedAt: string;
}

// ─── Project Phases ───

export interface ProjectPhase {
  id: string;
  projectId: string;
  name: string;
  description?: string;
  startDate?: string;
  endDate?: string;
  status: 'pending' | 'in-progress' | 'complete';
  sortOrder: number;
  /** Migration 053: the firm default_phases catalog row this project phase
   *  was seeded from. Present -> the row's `name` follows the catalog on
   *  rename (server-side cascade) and the catalog entry can't be deleted
   *  while rows still reference it. Absent (a project-specific extra phase,
   *  never in the firm catalog) -> never matched by name, left alone. */
  defaultPhaseId?: string | null;
  completedAt?: string | null;
  completedBy?: string | null;
  createdAt: string;
  updatedAt: string;
}

// ─── Project Scopes ───

export interface ProjectScope {
  id: string;
  firmId?: string;
  name: string;
  parentId?: string; // null = top-level scope
  color: string; // hex color for display
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface ProjectScopeAssignment {
  id: string;
  firmId?: string;
  projectId: string;
  scopeId: string;
  createdAt: string;
}

// ─── Deliverables ───

export interface Deliverable {
  id: string;
  projectId: string;
  name: string;
  description?: string;
  dueDate?: string;
  sealDate?: string;
  status: 'pending' | 'in-progress' | 'complete';
  issued: boolean;
  /** OPTIONAL tag only — a deliverable is name + due date + project and
   *  never requires a phase. No UI may group, gate, or filter deliverables
   *  by phase as a parent (decided 2026-07-20; billing phases are a separate
   *  concept used only where billing happens in-app). */
  phaseId?: string;
  createdAt: string;
  updatedAt: string;
}

// ─── Project Design Brief ───

/** One applicable code entry — the code itself plus an optional free-text
 *  note (edition, amendment, why it applies). */
export interface DesignBriefCode {
  code: string;
  note?: string;
}

/** One row per project (migration 051). Project Center R1's Design Brief
 *  tab: scope narrative, applicable code list, occupancy/construction
 *  classification, owner preferences, design criteria. Gated the same as
 *  `deliverables` (TABLE_PERMS.project_design_briefs -> `projects` cat). */
export interface ProjectDesignBrief {
  id: string;
  firmId?: string;
  projectId: string;
  scope?: string;
  applicableCodes: DesignBriefCode[];
  occupancy?: string;
  constructionType?: string;
  ownerPreferences?: string;
  designCriteria?: string;
  /** Migration 055. */
  squareFootage?: number;
  createdAt: string;
  updatedAt: string;
  updatedBy?: string;
}

/** One row per custom title/value field a PM adds to a project's Design
 *  Brief, beyond the fixed columns on ProjectDesignBrief (migration 055).
 *  Ordered by sortOrder within a project. Gated the same as
 *  ProjectDesignBrief (TABLE_PERMS.project_design_brief_fields ->
 *  `projects` cat, writes take projects.edit). */
export interface DesignBriefField {
  id: string;
  firmId?: string;
  projectId: string;
  title: string;
  value?: string;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

// ─── Custom Widgets (Project Center widget builder, T5 backend) ───

/** Structural duplicate of `TableFilterState`/`FilterRule`/`FilterGroup`
 *  from `src/renderer/components/filterModel.ts`. shared/ must not import
 *  from renderer/ — tsconfig.main.json's `include` is `src/main`,
 *  `src/shared`, `src/engine` only, so a real import would break
 *  `npm run build:main` ("file not under rootDir"). This shape is kept
 *  intentionally minimal and structurally identical, so a real
 *  `TableFilterState` value satisfies it with zero cast at the
 *  `widgetEvaluator` boundary. If filterModel's shape changes, update both. */
export type WidgetFilterOp =
  | 'contains'
  | 'notContains'
  | 'is'
  | 'isNot'
  | 'isEmpty'
  | 'isNotEmpty'
  | 'anyOf'
  | 'noneOf'
  | 'eq'
  | 'neq'
  | 'gt'
  | 'lt'
  | 'between'
  | 'after'
  | 'before'
  | 'betweenDates'
  | 'inNext'
  /** Widgets R2 Builder R2 — relative-date presets, evaluated against
   *  "now" at render time so a saved widget stays current. `overdue` is
   *  the date half only (before today) here; the "AND not complete" half
   *  is a per-source refinement `widgetEvaluator` applies using the
   *  matching `WidgetSource.completeField` (filterModel has no row/source
   *  context to know what "complete" means for an arbitrary table). */
  | 'today'
  | 'next7'
  | 'next30'
  | 'overdue'
  | 'thisMonth';

export interface WidgetFilterRule {
  field: string;
  op: WidgetFilterOp;
  value?: unknown;
}
export interface WidgetFilterGroup {
  match: 'all' | 'any';
  rules: WidgetFilterRule[];
}
export interface WidgetFilterState {
  version: 2;
  match: 'all' | 'any';
  rules: (WidgetFilterRule | WidgetFilterGroup)[];
}

/** One reduction over a source's (filtered) rows — shared by every shape
 *  that counts/sums/averages/mins/maxes a field. `'count'` needs no field. */
export type WidgetMeasure = 'count' | { op: 'sum' | 'avg' | 'min' | 'max'; field: string };

/** What a widget counts/lists/charts once its source rows are filtered.
 *  Builder R2 (Widgets R2 §D) adds `meter` and `line`; `number`/`bar` gain
 *  min/max via the shared `WidgetMeasure`; `list.max` is constrained to the
 *  three Sheet-offered page sizes (the evaluator still clamps a legacy/out-
 *  of-range stored value instead of throwing). */
export type CustomWidgetForm =
  | { kind: 'number'; measure: WidgetMeasure }
  | {
      kind: 'list';
      fields: string[];
      sort?: { field: string; dir: 'asc' | 'desc' };
      max?: 5 | 10 | 25;
    }
  | { kind: 'bar'; groupBy: string; measure: WidgetMeasure }
  | {
      kind: 'meter';
      measure: WidgetMeasure;
      target:
        | { kind: 'field'; field: string; op: 'sum' | 'avg' | 'max' | 'min' }
        | { kind: 'fixed'; value: number };
    }
  | { kind: 'line'; dateField: string; bucket: 'week' | 'month'; measure: WidgetMeasure };

/** Status/series identity for a widget's accent — structural duplicate of
 *  `ChartTone` in `components/widgets/WChart.tsx`. Kept here (shared/,
 *  zero React) so `CustomWidgetDefinition` can carry it without either
 *  side importing the other; update both if the tone set changes. */
export type WidgetTone = 'ok' | 'warn' | 'danger' | 'info' | 'neutral' | 'forecast' | 'accent';

/** Curated Lucide icon names the builder's icon picker offers — plain
 *  strings (no lucide-react import here; that lives in the Sheet/UI). */
export const CUSTOM_WIDGET_ICONS = [
  'LayoutGrid',
  'ListChecks',
  'Users',
  'FileText',
  'DollarSign',
  'Clock',
  'Calendar',
  'TrendingUp',
  'BarChart3',
  'PieChart',
  'Target',
  'Flag',
  'AlertTriangle',
  'CheckCircle2',
  'Receipt',
  'Package',
] as const;
export type CustomWidgetIcon = (typeof CUSTOM_WIDGET_ICONS)[number];

/** The full recipe for a user-built widget — which source, which rows
 *  (filter), and how to reduce them (form). Versioned so a future shape
 *  change can migrate old rows instead of breaking them. `icon`/`accent`
 *  are display metadata chosen in the builder Sheet; they live in the
 *  definition JSON (not on `CustomWidget` itself) so the whole recipe —
 *  data AND presentation — round-trips as one versioned blob. */
export interface CustomWidgetDefinition {
  version: 1;
  sourceId: string;
  filter: WidgetFilterState;
  form: CustomWidgetForm;
  icon?: CustomWidgetIcon;
  accent?: WidgetTone;
}

/** One row per user-built widget (migration 052). Personal until `shared`
 *  — same lifecycle as DashboardView (TABLE_PERMS.custom_widgets rides the
 *  `dashboards` category, owner_id in place of dashboard_views' user_id). */
export interface CustomWidget {
  id: string;
  firmId?: string;
  ownerId: string;
  /** Logical surface id this widget renders on (e.g. a project dashboard id). */
  surface: string;
  name: string;
  definition: CustomWidgetDefinition;
  shared?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

// ─── Hour Forecasts ───

export interface HourForecast {
  id: string;
  projectId: string;
  teamMemberId: string;
  phaseId?: string;
  weekStartDate: string;
  /** Per-day grain (added in migration 014). Older rows pre-grain have
   *  day_date = week_start_date so they still render under the daily renderer. */
  dayDate?: string;
  hours: number;
  notes?: string;
  /** When forecast was auto-created from another source (e.g. vacation),
   *  this records the origin so the UI can lock the cell and the system
   *  can keep it in sync. 'manual' = hand-entered (default). */
  sourceKind?: 'manual' | 'vacation';
  /** Source row id when sourceKind != 'manual' (e.g. vacation_request.id). */
  sourceId?: string;
  createdAt: string;
  updatedAt: string;
}

// ─── Forecast confirms (Time & Expense rebuild, Tranche 3 — meeting mode) ───

/** One "Time's good" stamp for a member's whole week, made in Forecasting's
 *  Meeting mode (migration 043). `confirmedBy` is always the CALLER who
 *  pressed confirm (server-stamped), which may differ from `teamMemberId`
 *  (a PM confirming someone else's row during a walkthrough). */
export interface ForecastConfirm {
  id: string;
  firmId?: string;
  teamMemberId: string;
  weekStartDate: string;
  confirmedBy: string;
  confirmedAt: string;
  createdAt?: string;
  updatedAt?: string;
}

// ─── Hour Entries ───

export interface HourEntry {
  id: string;
  projectId: string;
  teamMemberId: string;
  phaseId?: string;
  activityCodeId?: string;
  timesheetId?: string;
  overheadCategory?: string; // non-null = overhead entry (project-less)
  date: string;
  hours: number;
  note?: string;
  billable: boolean;
  invoiceId?: string;
  createdAt: string;
  updatedAt: string;
}

// ─── Expenses ───

export interface Expense {
  id: string;
  firmId?: string;
  teamMemberId: string;
  projectId?: string;
  expenseTypeId: string;
  date: string;
  quantity: number; // units (miles, count, etc.) or flat dollar amount
  rate?: number; // per-unit rate from expense type; null = flat amount
  total: number; // quantity * rate, or just quantity if no rate
  comment?: string;
  month: string; // YYYY-MM grouping key
  status: 'draft' | 'submitted' | 'approved' | 'rejected';
  submittedAt?: string;
  approvedBy?: string;
  approvedAt?: string;
  /** Tranche 0 item 2/7, server action expense_reject, migration 042. */
  rejectedBy?: string;
  rejectedAt?: string;
  rejectionReason?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ExpenseAttachment {
  id: string;
  expenseId: string;
  fileName: string;
  mimeType: string;
  data: string; // base64-encoded file data
  createdAt: string;
}

// ─── Invoices ───

export interface InvoiceLineItem {
  /** @deprecated use sourceIds[] for traceability — single hourEntryId
   *  is kept for back-compat with v1 invoices. */
  hourEntryId?: string;
  description: string;
  /** Existing field — kept as-is. New code should also populate `quantity`
   *  so non-time line items (fixed-fee, expenses) read cleanly. */
  hours: number;
  /** Generic quantity for non-time line items. Mirrors `hours` on time
   *  entries; for fixed-fee or expenses it's a unit count. */
  quantity?: number;
  rate: number;
  amount: number;
  /** Provenance of the line item — drives "show source" affordances and
   *  prevents the same hour entry being billed twice. */
  sourceKind?: 'time' | 'expense' | 'fixed_fee' | 'manual';
  /** All hour_entry / expense ids consumed by this line item. */
  sourceIds?: string[];
}

export interface Invoice {
  id: string;
  projectId: string;
  organizationId: string;
  phaseId?: string;
  invoiceNumber: string;
  /** Original invoice date (legacy). New code reads `issueDate` first;
   *  this is kept for older rows. */
  date: string;
  /** When the invoice was issued (lifecycle). May differ from `date`
   *  on rows that pre-date migration 014. */
  issueDate?: string;
  dueDate?: string;
  /** Date the invoice was marked sent. Driven by user action. */
  sentDate?: string;
  /** Date the invoice was fully paid. Set when status = 'paid'. */
  paidDate?: string;
  /** Who performed each lifecycle action (Tranche 0 item 2/7, server
   *  actions invoice_send/mark_paid/void, migration 042) — the CALLER,
   *  stamped server-side, never client-supplied. */
  sentBy?: string;
  paidBy?: string;
  voidedBy?: string;
  voidedAt?: string;
  lineItems: InvoiceLineItem[];
  subtotal: number;
  /** Tax in dollars (computed). */
  tax?: number;
  /** Tax rate in percent (input — drives `tax`). */
  taxRate?: number;
  total: number;
  /** Cumulative amount paid. status='partial' when 0 < paid < total. */
  amountPaid?: number;
  /** Status now spans the full lifecycle. Older rows stored only
   *  draft/sent/paid; the additional values come from migration 014. */
  status: 'draft' | 'sent' | 'partial' | 'paid' | 'void';
  notes?: string;
  /** Default payment terms (e.g. "Net 30"). Surfaced in PDF footer. */
  terms?: string;
  /** team_member id of the creator — drives audit trail. */
  createdBy?: string;
  pdfPath?: string;
  archivedAt?: string | null;
  archivedBy?: string | null;
  createdAt: string;
  updatedAt: string;
}

// ─── Project Drive Scan Result ───

/** Parsed entry from scanning a Project Drive root — a project-number-prefixed folder. */
export interface ProjectDriveFolder {
  folderName: string;
  folderPath: string;
  projectNumber: string;
  namePart: string;
}

// ─── Tasks ───

export type TaskStatus = 'todo' | 'in_progress' | 'review' | 'done';
export type TaskPriority = 'low' | 'medium' | 'high' | 'urgent';

export interface Task {
  id: string;
  firmId?: string;
  title: string;
  description?: string;
  status: TaskStatus;
  priority: TaskPriority;
  /** @deprecated Tasks Kit Decision 20 — task_project_links is the only
   *  project truth now. The column stays (server migration 057 backfilled
   *  every existing value into a link and nulls it on write); nothing in
   *  the renderer reads or writes this field any more. */
  projectId?: string;
  phaseId?: string; // optional link to project phase
  parentTaskId?: string; // self-ref for subtasks
  createdBy: string; // team member id
  dueDate?: string; // ISO date
  tags?: string[];
  archivedAt?: string | null;
  archivedBy?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface TaskAssignment {
  id: string;
  firmId?: string;
  taskId: string;
  teamMemberId: string;
  assignedBy: string; // team member id who assigned
  assignedAt: string;
}

export interface TaskComment {
  id: string;
  firmId?: string;
  taskId: string;
  authorId: string; // team member id
  text: string;
  createdAt: string;
  updatedAt: string;
}

export type TaskNotificationType =
  | 'assigned'
  | 'comment'
  | 'status_change'
  | 'due_soon'
  | 'completed';

export interface TaskNotification {
  id: string;
  firmId?: string;
  taskId: string;
  teamMemberId: string; // who receives the notification
  type: TaskNotificationType;
  message: string;
  read: boolean;
  createdAt: string;
}

export interface TaskLink {
  id: string;
  firmId?: string;
  taskId: string;
  url: string;
  label?: string;
  createdAt: string;
}

export interface TaskAttachment {
  id: string;
  firmId?: string;
  taskId: string;
  fileName: string;
  mimeType: string;
  data: string; // base64-encoded file data
  createdAt: string;
}

export interface TaskProjectLink {
  id: string;
  firmId?: string;
  taskId: string;
  projectId: string;
  createdAt: string;
}
